import React from 'react';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import i18n from '../../i18n';
import { ReactComponent as SuccessFilledIcon } from '../../static/images/svg/ep_success-filled.svg';
import { ReactComponent as ErrorRoundedIcon } from '../../static/images/svg/material-symbols_error-rounded.svg';
import './scss/menu-library-flow-dialogs.style.scss';

/** 上传结果紫条：主文案 + 可选日期 */
export type MenuLibraryFlowResultChipItem = {
    /** 主文案（菜单名） */
    primary: string;
    /** 副文案（如 `DD/MM/YYYY`） */
    secondary?: string;
};

/** 流程结果弹窗列表形态 */
export type MenuLibraryFlowResultListVariant = 'chips' | 'numbered';

/** 结果态：成功绿勾 / 失败红叹号 */
export type MenuLibraryFlowResultStatus = 'success' | 'failure';

/** 流程结果弹窗 props */
export type MenuLibraryFlowResultDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭（确定 / Esc） */
    onClose: () => void;
    /** 标题（可含 Trans 高亮） */
    title: React.ReactNode;
    /** 摘要区（加载成功路径等；上传结果可不传） */
    summary?: React.ReactNode;
    /** 列表形态：chips=上传紫条；numbered=加载浅底编号列表 */
    listVariant: MenuLibraryFlowResultListVariant;
    /** chips 列表项 */
    chipItems?: MenuLibraryFlowResultChipItem[];
    /** numbered 列表项（菜品名等） */
    numberedItems?: string[];
    /** 编号列表下方的附加内容（如套餐下新建菜分区） */
    extraContent?: React.ReactNode;
    /** 确定按钮文案 */
    confirmLabel?: string;
    /** 成功 / 失败图标与强调色；默认 success */
    status?: MenuLibraryFlowResultStatus;
};

/**
 * 云端流程「结果」浮层：标题 + 成功/失败图标 + 可选摘要 + 列表 + 确定（上传 `9806:50724` / 加载 `9782:49542`）。
 */
const MenuLibraryFlowResultDialog: React.FC<MenuLibraryFlowResultDialogProps> = ({
    open,
    onClose,
    title,
    summary,
    listVariant,
    chipItems = [],
    numberedItems = [],
    extraContent,
    confirmLabel,
    status = 'success',
}) => {
    // 确定按钮文案
    const okLabel = confirmLabel ?? i18n.t('general_button_confirm');
    // 是否有可展示列表
    const hasChips = listVariant === 'chips' && chipItems.length > 0;
    const hasNumbered = listVariant === 'numbered' && numberedItems.length > 0;
    // 是否失败态（0 道成功等）
    const isFailure = status === 'failure';

    const paperClassName = [
        'menu-library-flow-result-dialog__paper',
        listVariant === 'numbered' ? 'menu-library-flow-result-dialog__paper--numbered' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const iconClassName = [
        'menu-library-flow-result-dialog__icon',
        isFailure ? 'menu-library-flow-result-dialog__icon--failure' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            className="menu-library-flow-result-dialog"
            PaperProps={{ className: paperClassName }}
        >
            <DialogContent className="menu-library-flow-result-dialog__content">
                <Typography variant="h3" component="h2" className="menu-library-flow-result-dialog__title">
                    {title}
                </Typography>
                {isFailure ? (
                    <ErrorRoundedIcon className={iconClassName} aria-hidden />
                ) : (
                    <SuccessFilledIcon className={iconClassName} aria-hidden />
                )}
                {summary != null ? (
                    <Typography
                        variant="subtitle2"
                        component="div"
                        className="menu-library-flow-result-dialog__summary"
                    >
                        {summary}
                    </Typography>
                ) : null}
                {hasChips ? (
                    <Box className="menu-library-flow-result-dialog__chips" component="ul">
                        {chipItems.map((item, index) => (
                            <Box
                                key={`${item.primary}-${item.secondary ?? ''}-${index}`}
                                component="li"
                                className="menu-library-flow-result-dialog__chip"
                            >
                                <Typography
                                    component="span"
                                    variant="body1"
                                    className="menu-library-flow-result-dialog__chip-primary"
                                >
                                    {item.primary}
                                </Typography>
                                {item.secondary ? (
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        className="menu-library-flow-result-dialog__chip-secondary"
                                    >
                                        {item.secondary}
                                    </Typography>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                ) : null}
                {hasNumbered ? (
                    <Box className="menu-library-flow-result-dialog__numbered">
                        {numberedItems.map((name, index) => (
                            <Typography
                                key={`${name}-${index}`}
                                component="div"
                                variant="body1"
                                className="menu-library-flow-result-dialog__numbered-item"
                                title={name}
                            >
                                {`${index + 1}. ${name}`}
                            </Typography>
                        ))}
                    </Box>
                ) : null}
                {extraContent != null ? extraContent : null}
                <Button
                    variant="contained"
                    color="primary"
                    className="menu-library-flow-result-dialog__ok"
                    onClick={onClose}
                >
                    <Typography component="span" variant="h4">
                        {okLabel}
                    </Typography>
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default MenuLibraryFlowResultDialog;
